import React, { Fragment, useEffect, useLayoutEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { middleDotSeparatorMessage } from 'LuxKit/intl/separators'
import styled from 'styled-components'
import { rem } from 'polished'
import Message from 'supportAssistant/components/Chat/Message'
import MessagesLoadingSkeleton from './MessagesLoadingSkeleton'
import BodyText from 'LuxKit/Typography/BodyText'
import moment from 'moment/moment'
import Loading from 'components/Common/Loading/LoadingAnimation'
import Group from 'components/utils/Group'
import LuxuryEscapesSmallLogo from 'components/App/Header/Logo/logos/LuxuryEscapesSmallLogo'

const WAITING_MESSAGE_DELAY = 8000

const DateTimeContainer = styled.div<{ $compact?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${rem(6)};
  margin-bottom: ${props => rem(props.$compact ? 4 : 8)};
  margin-top: ${rem(16)};
`

const MessageContent = styled.div`
  padding: ${rem(8)};
  border-radius: ${rem(4)};
  background-color: ${props => props.theme.palette.neutral.default.seven};
  color: ${props => props.theme.palette.neutral.default.one};
  width: max-content;
`

const TypingIndicatorContainer = styled(Group)`
  margin-top: ${rem(16)};
  margin-left: ${rem(8)};
`

const TypingAvatar = styled.div`
  width: ${rem(24)};
  height: ${rem(24)};
  flex-shrink: 0;
`

interface Props {
  messages: Array<App.ChatMessage>
  loadingMessages?: boolean
  isTyping: boolean
  isAgentTyping: boolean
  onMessagesRendered?: () => void
  compact?: boolean
}

function MessagesListContainer(props: Props) {
  const { messages, loadingMessages, isTyping, isAgentTyping, onMessagesRendered, compact } = props
  const intl = useIntl()

  const [isTypingGtWait, setIsTypingGtWait] = useState(false)

  // Epoch date 1970, so first message will always be different and therefore display date/time
  let previousDate = moment(0).format('D MMMM YYYY')

  // if isTyping has been true for longer than 5 seconds show text
  useEffect(() => {
    if (isTyping) {
      const timeout = setTimeout(() => {
        setIsTypingGtWait(true)
      }, WAITING_MESSAGE_DELAY)
      return () => clearTimeout(timeout)
    } else {
      setIsTypingGtWait(false)
    }
  }, [isTyping])

  // notify parent when messages have been rendered to the DOM
  useLayoutEffect(() => {
    if (!loadingMessages && messages.length > 0 && onMessagesRendered) {
      onMessagesRendered()
    }
  }, [loadingMessages, messages.length, onMessagesRendered])

  return (
    <Group direction="vertical" gap={compact ? 8 : 12} fullWidth>
      {loadingMessages && <MessagesLoadingSkeleton />}
      {!loadingMessages && <>
        {messages?.map((message, index) => {
          const messageDate = moment(message.createdAt).format('D MMMM YYYY')

          if (messageDate !== previousDate) {
            previousDate = messageDate

            return (
              <Fragment key={`date-${message.createdAt}-${message.id ?? index}`}>
                <DateTimeContainer $compact={compact}>
                  {index === 0 && <BodyText variant="small">{moment(message.createdAt).format('h:mm a')}</BodyText>}
                  {index === 0 && <BodyText variant="small" colour="neutral-three">{intl.formatMessage(middleDotSeparatorMessage)}</BodyText>}
                  <BodyText variant="small">{previousDate}</BodyText>
                </DateTimeContainer>
                <Message
                  message={message}
                  key={`${message.createdAt}-${message.id ?? index}`}
                />
              </Fragment>
            )
          }

          return (
            <Message
              message={message}
              key={`${message.createdAt}-${message.id ?? index}`}
            />
          )
        })}
      </>}
      {isTypingGtWait &&
        <Message
          message={{
            sender: 'chatbot',
            text: intl.formatMessage({
              id: 'Fg93SJiFlB',
              defaultMessage: 'We\u2019re searching for a response to your question, thank you for your patience',
              description: 'Message shown when chatbot response is taking longer than expected',
            }),
          }}
        />}
      {(isTyping || isAgentTyping) && (
        <TypingIndicatorContainer direction="horizontal" verticalAlign="end" gap={8}>
          <TypingAvatar>
            <LuxuryEscapesSmallLogo height={24} width={24} />
          </TypingAvatar>
          <MessageContent>
            <Loading size={6} />
          </MessageContent>
        </TypingIndicatorContainer>
      )}
    </Group>
  )
}

export default MessagesListContainer
